import { useEffect, useState } from "react";

import {
  Box,
  Typography,
  Paper,
  Stack,
  Chip,
  Alert,
} from "@mui/material";

import { API_URL } from "../config/api";

type VersionEntry = {
  version: string;
  date: string;
  notes?: string[];
  current: boolean;
};

type VersionsResponse = {
  current: string;
  startedAt?: string;
  latest: string;
  upToDate: boolean;
  history: VersionEntry[];
};

export default function VersionHistory() {
  const [data, setData] = useState<VersionsResponse | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    fetch(`${API_URL}/versions`)
      .then((response) => {
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }
        return response.json();
      })
      .then(setData)
      .catch(() => setError(true));
  }, []);

  if (error) {
    return (
      <Alert severity="error">
        Impossible de récupérer les versions du serveur. Le service
        tourne peut-être avec une version antérieure au versionnement :
        redémarre-le pour charger la dernière version.
      </Alert>
    );
  }

  if (!data) {
    return <Typography>Chargement des versions...</Typography>;
  }

  return (
    <Box>
      <Typography variant="h5" fontWeight="bold" gutterBottom>
        Versions du serveur
      </Typography>

      <Typography color="text.secondary" sx={{ mb: 2 }}>
        Version en cours : <strong>v{data.current}</strong>
        {data.startedAt &&
          ` — service démarré le ${new Date(data.startedAt).toLocaleString("fr-BE")}`}
      </Typography>

      {!data.upToDate && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          La version v{data.latest} est disponible dans le code, mais le
          service tourne encore en v{data.current}. Redémarre le service
          OpenJudoSV-Backend pour la charger.
        </Alert>
      )}

      <Stack spacing={1.5}>
        {data.history.map((entry) => (
          <Paper
            key={entry.version}
            sx={{
              p: 2,
              borderLeft: "5px solid",
              borderLeftColor: entry.current
                ? "success.main"
                : "divider",
            }}
          >
            <Stack
              direction="row"
              spacing={1.5}
              alignItems="center"
              sx={{ mb: entry.notes?.length ? 1 : 0 }}
            >
              <Typography variant="h6" fontWeight="bold">
                v{entry.version}
              </Typography>
              <Typography color="text.secondary">
                {new Date(entry.date).toLocaleDateString("fr-BE")}
              </Typography>
              {entry.current && (
                <Chip
                  label="Version actuelle"
                  color="success"
                  size="small"
                />
              )}
            </Stack>

            {entry.notes && entry.notes.length > 0 && (
              <Box component="ul" sx={{ m: 0, pl: 3 }}>
                {entry.notes.map((note) => (
                  <Typography component="li" variant="body2" key={note}>
                    {note}
                  </Typography>
                ))}
              </Box>
            )}
          </Paper>
        ))}
      </Stack>
    </Box>
  );
}
